import imageUrlBuilder from "@sanity/image-url";
import { createServerFn } from "@tanstack/react-start";

import { listNewsletters } from "@/lib/beehiiv";
import { sanityClient } from "@/sanity-client";

export const getPosts = createServerFn().handler(async () => {
  const builder = imageUrlBuilder(sanityClient);
  const data = await sanityClient.fetch<
    {
      title: string;
      description: string;
      slug: { current: string };
      publishedAt: Date;
      _createdAt: string;
      tags: string[];
      image: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      body: any;
      author: {
        name: string;
        bio: string;
        handle: string;
        avatar: string;
      };
    }[]
  >(
    `*[_type == "post" && defined(slug)]{
    title,
    description,
    slug,
    tags,
    image,
    body,
    _createdAt,
    "author": author->{
      name,
      bio,
      avatar,
      handle,
      publishedAt
    }
  } | order(dateTime(_createdAt) desc)`,
  );

  const newsletters = await listNewsletters();

  const sanityPosts = data.map((post) => ({
    ...post,
    image: builder.image(post.image).width(1000).url(),
    publishedAt: new Date(post._createdAt),
    author: {
      ...post.author,
      avatar: builder.image(post.author.avatar).width(100).url(),
    },
  }));

  type SanityPost = (typeof sanityPosts)[number];

  const defaultAvatar = sanityPosts.find(
    (p) => p.author.handle === "graze.social",
  )?.author.avatar;

  const posts = [
    ...sanityPosts,
    ...newsletters.map(
      (newsletter, index): SanityPost => ({
        title: newsletter.title,
        description: `Newsletter #${newsletters.length - index}`,
        slug: { current: newsletter.slug },
        publishedAt: new Date(newsletter.publish_date * 1000),
        _createdAt: new Date(newsletter.publish_date * 1000).toISOString(),
        image: newsletter.thumbnail_url,
        author: {
          name: "Graze Team",
          bio: "",
          handle: "graze.social",
          avatar: defaultAvatar ?? "",
        },
        body: newsletter.content.free.web,
        tags: ["newsletter"],
      }),
    ),
  ] as SanityPost[];

  return posts.sort(
    (a, b) => b.publishedAt.getTime() - a.publishedAt.getTime(),
  );
});

export interface DocData {
  title: string;
  order: number;
  description: string;
  slug: { current: string };
  publishedAt: Date;
  tags: string[];
  image: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any;
  author: {
    name: string;
    bio: string;
    handle: string;
    avatar: string;
  };
}

export const getDocs = createServerFn().handler(async () => {
  const builder = imageUrlBuilder(sanityClient);
  const data = await sanityClient.fetch<DocData[]>(
    `*[_type == "docs" && defined(slug)]{
    title,
    description,
    slug,
    tags,
    body[]{
      ...,
      _type == "image" => {
        ...,
        asset->{
          ...,
          "_key": _id,
          url
        }
      }
    },
    order,
    "author": author->{
      name,
      bio,
      avatar,
      handle,
      publishedAt
    }
  } | order(publishedAt desc)`,
  );
  return data
    .map((post) => ({
      ...post,
      author: {
        ...post.author,
        avatar: builder.image(post.author.avatar).width(100).url(),
      },
    }))
    .sort((a, b) => a.order - b.order);
});
